/**
 * Job Assignment Page - Enterprise Job Management
 * Advanced job assignment with drag-and-drop dispatching, route optimization,
 * skill matching, and real-time technician coordination
 */

import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { AdminPageLayout, StatsGrid } from '@/components/layout/AdminPageLayout';
import { StatCard } from '@/components/layout/AdminStatCard';
import { Card } from '@/components/ui/card';
import {
  MapPin,
  Clock,
  Users,
  Route,
  Phone,
  MessageSquare,
  Calendar,
  DollarSign,
  CheckCircle,
  AlertTriangle,
  Truck,
  Navigation,
  Zap,
  Filter,
  Search,
  Plus,
  Settings,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

interface Technician {
  id: string;
  name: string;
  status: 'available' | 'busy' | 'offline' | 'break';
  currentLocation?: {
    latitude: number;
    longitude: number;
    address: string;
  };
  currentJob?: string;
  skillSet: string[];
  performance: {
    jobsCompleted: number;
    rating: number;
    onTimeRate: number;
  };
  todaysJobs: number;
  efficiency: number;
  capacity: number;
  workload: number;
}

interface Job {
  id: string;
  title: string;
  customer: string;
  address: string;
  priority: 'low' | 'medium' | 'high' | 'emergency';
  status: 'unassigned' | 'assigned' | 'in_progress' | 'completed' | 'cancelled';
  scheduledTime: string;
  estimatedDuration: number;
  skillRequired: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  technicianId?: string;
  urgency: number;
  jobValue: number;
  description: string;
  requirements: string[];
  specialInstructions?: string;
}

interface AssignmentSuggestion {
  jobId: string;
  technicianId: string;
  score: number;
  reasons: string[];
  estimatedTravelTime: number;
  skillMatch: number;
  availabilityScore: number;
}

export default function JobAssignmentPage() {
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [unassignedJobs, setUnassignedJobs] = useState<Job[]>([]);
  const [assignmentSuggestions, setAssignmentSuggestions] = useState<AssignmentSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState<string | null>(null);
  const [selectedTechnician, setSelectedTechnician] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'board' | 'list' | 'map'>('board');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAssignmentData();
    generateAssignmentSuggestions();
  }, []);

  const loadAssignmentData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadTechnicians(),
        loadJobs()
      ]);
    } catch (error) {
      console.error('Error loading assignment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    // Enhanced mock data with capacity and workload
    const mockTechnicians: Technician[] = [
      {
        id: '1',
        name: 'Mike Johnson',
        status: 'available',
        currentLocation: { latitude: 40.7128, longitude: -74.0060, address: '123 Main St, NYC' },
        skillSet: ['HVAC', 'Plumbing', 'Electrical'],
        performance: { jobsCompleted: 8, rating: 4.8, onTimeRate: 95 },
        todaysJobs: 6,
        efficiency: 92,
        capacity: 8,
        workload: 6
      },
      {
        id: '2',
        name: 'Sarah Wilson',
        status: 'busy',
        currentLocation: { latitude: 40.7589, longitude: -73.9851, address: '456 Oak Ave, NYC' },
        currentJob: 'Emergency Repair - Unit 5B',
        skillSet: ['Plumbing', 'Emergency Response'],
        performance: { jobsCompleted: 12, rating: 4.9, onTimeRate: 98 },
        todaysJobs: 8,
        efficiency: 96,
        capacity: 10,
        workload: 8
      },
      {
        id: '3',
        name: 'David Chen',
        status: 'available',
        currentLocation: { latitude: 40.7505, longitude: -73.9934, address: '789 Pine St, NYC' },
        skillSet: ['Electrical', 'Smart Home'],
        performance: { jobsCompleted: 6, rating: 4.7, onTimeRate: 88 },
        todaysJobs: 4,
        efficiency: 85,
        capacity: 6,
        workload: 4
      }
    ];
    setTechnicians(mockTechnicians);
  };

  const loadJobs = async () => {
    // Enhanced mock data with more details
    const mockJobs: Job[] = [
      {
        id: '1',
        title: 'HVAC System Installation',
        customer: 'John Smith',
        address: '123 Main St, Apartment 5B',
        priority: 'high',
        status: 'unassigned',
        scheduledTime: '2024-01-15T09:00:00Z',
        estimatedDuration: 180,
        skillRequired: ['HVAC'],
        coordinates: { latitude: 40.7128, longitude: -74.0060 },
        urgency: 8,
        jobValue: 2500,
        description: 'Complete HVAC system replacement for 2-bedroom apartment',
        requirements: ['EPA Certification', '2-person team'],
        specialInstructions: 'Building requires elevator reservation'
      },
      {
        id: '2',
        title: 'Emergency Plumbing Repair',
        customer: 'Jane Doe',
        address: '456 Oak Ave, Unit 12',
        priority: 'emergency',
        status: 'assigned',
        scheduledTime: '2024-01-15T14:30:00Z',
        estimatedDuration: 90,
        skillRequired: ['Plumbing', 'Emergency Response'],
        coordinates: { latitude: 40.7589, longitude: -73.9851 },
        technicianId: '2',
        urgency: 10,
        jobValue: 800,
        description: 'Burst pipe in kitchen causing flooding',
        requirements: ['Emergency Response Certified'],
        specialInstructions: 'Water shutoff required before work begins'
      },
      {
        id: '3',
        title: 'Electrical Panel Upgrade',
        customer: 'Bob Johnson',
        address: '789 Pine St, House',
        priority: 'medium',
        status: 'unassigned',
        scheduledTime: '2024-01-15T10:00:00Z',
        estimatedDuration: 240,
        skillRequired: ['Electrical'],
        coordinates: { latitude: 40.7505, longitude: -73.9934 },
        urgency: 6,
        jobValue: 1800,
        description: 'Upgrade from 100A to 200A electrical service',
        requirements: ['Licensed Electrician', 'City Permit'],
        specialInstructions: 'Requires city inspection after completion'
      }
    ];

    setJobs(mockJobs);
    setUnassignedJobs(mockJobs.filter(job => job.status === 'unassigned'));
  };

  const generateAssignmentSuggestions = async () => {
    // AI-powered assignment suggestions based on:
    // - Skill matching
    // - Location proximity
    // - Technician availability
    // - Performance history
    // - Customer preferences

    const suggestions: AssignmentSuggestion[] = [
      {
        jobId: '1',
        technicianId: '1',
        score: 95,
        reasons: ['Perfect skill match', 'Available now', 'High performance rating'],
        estimatedTravelTime: 15,
        skillMatch: 100,
        availabilityScore: 90
      },
      {
        jobId: '3',
        technicianId: '3',
        score: 88,
        reasons: ['Exact skill match', 'Nearby location', 'Good availability'],
        estimatedTravelTime: 8,
        skillMatch: 100,
        availabilityScore: 76
      }
    ];

    setAssignmentSuggestions(suggestions);
  };

  const assignJobToTechnician = async (jobId: string, technicianId: string) => {
    // Update job assignment
    setJobs(prev => prev.map(job =>
      job.id === jobId
        ? { ...job, status: 'assigned', technicianId }
        : job
    ));

    setUnassignedJobs(prev => prev.filter(job => job.id !== jobId));

    // Update technician workload
    setTechnicians(prev => prev.map(tech =>
      tech.id === technicianId
        ? { ...tech, workload: tech.workload + 1, status: 'busy' as const }
        : tech
    ));

    // Log assignment
    console.log(`Job ${jobId} assigned to technician ${technicianId}`);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'bg-green-100 text-green-800';
      case 'busy': return 'bg-blue-100 text-blue-800';
      case 'offline': return 'bg-gray-100 text-gray-600';
      case 'break': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'emergency': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getWorkloadColor = (workload: number, capacity: number) => {
    const ratio = workload / capacity;
    if (ratio >= 1) return 'text-red-600';
    if (ratio >= 0.8) return 'text-orange-600';
    return 'text-green-600';
  };

  const filteredJobs = unassignedJobs.filter(job => {
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         job.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === 'all' || job.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });

  return (
    <AdminPageLayout
      title="Job Assignment Center"
      subtitle="AI-Powered Job Assignment • Route Optimization • Real-time Technician Coordination"
      actions={
        <div className="flex gap-2">
          <select className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
            <option>Bulk Actions</option>
            <option>Auto-Assign All</option>
            <option>Optimize Routes</option>
            <option>Export Assignments</option>
          </select>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium flex items-center gap-2">
            <Route className="w-4 h-4" />
            Optimize Routes
          </button>
        </div>
      }
    >
      {/* Assignment Overview Stats */}
      <StatsGrid cols={4}>
        <StatCard
          label="Unassigned Jobs"
          value={unassignedJobs.length}
          icon={AlertTriangle}
          subtitle="Need assignment"
        />
        <StatCard
          label="Available Techs"
          value={technicians.filter(t => t.status === 'available').length}
          icon={Users}
          subtitle={`${technicians.filter(t => t.status === 'busy').length} busy`}
        />
        <StatCard
          label="AI Suggestions"
          value={assignmentSuggestions.length}
          icon={Zap}
          subtitle="Smart assignments ready"
        />
        <StatCard
          label="Avg Assignment Time"
          value="2.3 min"
          icon={Clock}
          subtitle="From job creation"
        />
      </StatsGrid>

      {/* Main Assignment Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Job Queue */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold text-gray-900">Unassigned Jobs</h2>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search jobs..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 w-48"
                  />
                </div>
                <select
                  value={filterPriority}
                  onChange={(e) => setFilterPriority(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Priorities</option>
                  <option value="emergency">Emergency</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading jobs...</div>
            ) : filteredJobs.length === 0 ? (
              <div className="py-8 text-center text-gray-400">
                No unassigned jobs match your criteria.
              </div>
            ) : (
              <div className="space-y-3">
                {filteredJobs.map((job) => (
                  <div
                    key={job.id}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      selectedJob === job.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('jobId', job.id);
                    }}
                    onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900">{job.title}</h3>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityColor(job.priority)}`}>
                            {job.priority}
                          </span>
                          <span className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                            ${job.jobValue}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{job.customer} • {job.address}</p>
                        <p className="text-xs text-gray-500 mb-2">{job.description}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(job.scheduledTime).toLocaleTimeString()} • {job.estimatedDuration}min
                          </span>
                          <span className={`flex items-center gap-1 ${job.urgency >= 8 ? 'text-red-600' : job.urgency >= 6 ? 'text-orange-600' : 'text-green-600'}`}>
                            <AlertTriangle className="w-3 h-3" />
                            Urgency: {job.urgency}/10
                          </span>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <button className="p-2 text-gray-400 hover:text-blue-600">
                          <Eye className="w-4 h-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-green-600">
                          <Zap className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {job.skillRequired.length > 0 && (
                      <div className="flex gap-1 mb-2">
                        {job.skillRequired.map((skill, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded">
                            {skill}
                          </span>
                        ))}
                      </div>
                    )}

                    {selectedJob === job.id && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Requirements</h4>
                            <ul className="text-xs text-gray-600 space-y-1">
                              {job.requirements.map((req, index) => (
                                <li key={index}>• {req}</li>
                              ))}
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-2">Special Instructions</h4>
                            <p className="text-xs text-gray-600">{job.specialInstructions}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm">
                            Quick Assign
                          </button>
                          <button className="px-3 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 text-sm">
                            Manual Assign
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        {/* Technician Board */}
        <div className="lg:col-span-2">
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Available Technicians</h2>

            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading technicians...</div>
            ) : (
              <div className="space-y-4">
                {technicians.map((technician) => (
                  <div
                    key={technician.id}
                    className={`border rounded-lg p-4 transition-all ${
                      selectedTechnician === technician.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedTechnician(selectedTechnician === technician.id ? null : technician.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      const jobId = e.dataTransfer.getData('jobId');
                      if (jobId) {
                        assignJobToTechnician(jobId, technician.id);
                      }
                    }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${
                          technician.status === 'available' ? 'bg-green-500' :
                          technician.status === 'busy' ? 'bg-blue-500' :
                          technician.status === 'break' ? 'bg-yellow-500' :
                          'bg-gray-400'
                        }`} />
                        <div>
                          <h3 className="font-medium text-gray-900">{technician.name}</h3>
                          <p className="text-xs text-gray-600 capitalize">{technician.status}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-sm font-medium ${getWorkloadColor(technician.workload, technician.capacity)}`}>
                          {technician.workload}/{technician.capacity}
                        </div>
                        <div className="text-xs text-gray-500">jobs today</div>
                      </div>
                    </div>

                    {technician.currentLocation && (
                      <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                        <MapPin className="w-3 h-3" />
                        <span>{technician.currentLocation.address}</span>
                      </div>
                    )}

                    <div className="grid grid-cols-3 gap-2 text-xs text-gray-600 mb-3">
                      <div>Today's Jobs: {technician.todaysJobs}</div>
                      <div>Efficiency: {technician.efficiency}%</div>
                      <div>Rating: {technician.performance.rating}⭐</div>
                    </div>

                    <div className="flex gap-1 mb-2">
                      {technician.skillSet.map((skill, index) => (
                        <span key={index} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">
                          {skill}
                        </span>
                      ))}
                    </div>

                    {selectedTechnician === technician.id && (
                      <div className="mt-3 pt-3 border-t">
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Performance</h4>
                            <div className="text-xs text-gray-600">
                              <div>On-Time Rate: {technician.performance.onTimeRate}%</div>
                              <div>Jobs Completed: {technician.performance.jobsCompleted}</div>
                            </div>
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-900 mb-1">Current Status</h4>
                            <div className="text-xs text-gray-600">
                              <div>Status: <span className="capitalize">{technician.status}</span></div>
                              {technician.currentJob && <div>Current: {technician.currentJob}</div>}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                            View Schedule
                          </button>
                          <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                            Call
                          </button>
                          <button className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                            Message
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* AI Assignment Suggestions */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">AI Assignment Suggestions</h2>
          <button className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 font-medium flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Auto-Assign All
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {assignmentSuggestions.map((suggestion, index) => {
            const job = jobs.find(j => j.id === suggestion.jobId);
            const technician = technicians.find(t => t.id === suggestion.technicianId);

            if (!job || !technician) return null;

            return (
              <div key={index} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-900">Smart Match</span>
                  </div>
                  <span className="px-2 py-1 text-xs bg-purple-200 text-purple-700 rounded font-medium">
                    {suggestion.score}% match
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="text-sm">
                    <span className="font-medium text-gray-900">{job.title}</span>
                    <p className="text-xs text-gray-600">→ {technician.name}</p>
                  </div>

                  <div className="text-xs text-gray-600">
                    <div>Travel Time: {suggestion.estimatedTravelTime} min</div>
                    <div>Skill Match: {suggestion.skillMatch}%</div>
                    <div>Availability: {suggestion.availabilityScore}%</div>
                  </div>
                </div>

                <div className="space-y-1 mb-4">
                  {suggestion.reasons.map((reason, idx) => (
                    <div key={idx} className="text-xs text-purple-700">• {reason}</div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => assignJobToTechnician(suggestion.jobId, suggestion.technicianId)}
                    className="flex-1 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 text-xs"
                  >
                    Assign Now
                  </button>
                  <button className="px-3 py-1 bg-white border border-purple-300 text-purple-700 rounded hover:bg-purple-50 text-xs">
                    View Details
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </AdminPageLayout>
  );
}
